import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { validateIndianMobile } from '../utils/helpers';
import { downloadFile } from '../utils/downloadFile';
import { Upload, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '', dob: '', date_of_marriage: '', gender: 'Male',
  pan: '', aadhaar: '', address: '', primary_mobile: '',
  secondary_mobile: '', email: '', nominee: '', client_type: 'Individual',
  financial_holdings: { mutual_funds: 'unknown', loans: 'unknown', fd: 'unknown', rd: 'unknown', post_office: 'unknown', ppf: 'unknown' },
};

/**
 * Global "Add Client" dialog. Owned by GlobalDialogsProvider so the top-bar
 * button opens THIS from every page (not just Clients). Includes the same
 * inline "Add a policy now?" toggle used by the Clients page dialog — on
 * successful save, if toggled, the provider immediately opens Add Policy.
 */
const AddClientDialog = ({ open, onClose, onCreated, onRequestFollowUpPolicy }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [addPolicyToggle, setAddPolicyToggle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  // Per-client KYC document staging — files queued here get POSTed to
  // /api/clients/{new_client_id}/kyc after the client row is created.
  const [kycFiles, setKycFiles] = useState([]);
  const [kycUploading, setKycUploading] = useState(false);
  const kycInputRef = useRef(null);

  const undoBatch = async (batchId) => {
    try {
      const { data } = await axios.delete(`${API}/imports/${batchId}`, { withCredentials: true });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Rolled back ${total} row(s) from that batch`);
      setBulkResult(null);
      if (onCreated) onCreated({ bulkInserted: -total });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    }
  };

  const downloadTemplate = async () => {
    const res = await downloadFile('/templates/client.xlsx', 'client_template.xlsx');
    if (!res.ok) {
      console.error('Client template download failed:', res.error);
      toast.error(res.error?.toLowerCase().includes('auth') || res.error?.toLowerCase().includes('token')
        ? 'Your session expired — please sign in again and retry.'
        : `Could not download template: ${res.error}`);
    } else {
      toast.success('Client template downloaded');
    }
  };

  const uploadBulk = async () => {
    if (!bulkFile) { toast.error('Choose an Excel file first'); return; }
    setBulkUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const { data } = await axios.post(`${API}/clients/bulk-upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      if (data.inserted > 0 && (data.errors || []).length === 0) {
        toast.success(`Imported ${data.inserted} of ${data.total_rows} client(s)`, {
          action: data.batch_id ? { label: 'Undo', onClick: () => undoBatch(data.batch_id) } : undefined,
          duration: 12000,
        });
      } else if (data.inserted > 0) {
        toast.warning(`Imported ${data.inserted} · ${(data.errors || []).length} row(s) had issues — see details below.`, {
          action: data.batch_id ? { label: 'Undo', onClick: () => undoBatch(data.batch_id) } : undefined,
          duration: 12000,
        });
      } else {
        toast.error(`No rows imported — ${(data.errors || []).length} error(s). See details below.`);
      }
      if (onCreated && data.inserted > 0) onCreated({ bulkInserted: data.inserted });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' · ') : `HTTP ${err.response?.status || '?'} — ${err.message}`);
      setBulkResult({ inserted: 0, skipped: 0, total_rows: 0, errors: [msg] });
      toast.error(`Bulk upload failed: ${msg}`);
    } finally { setBulkUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mv = validateIndianMobile(formData.primary_mobile);
    if (!mv.ok) { toast.error(mv.error); return; }
    setSaving(true);
    try {
      const payload = { ...formData, primary_mobile: mv.digits };
      const { data } = await axios.post(`${API}/clients`, payload, { withCredentials: true });
      toast.success('Client created');
      // Upload any attached KYC docs — one at a time so per-file failures don't block the rest.
      if (kycFiles.length > 0 && data?.client_id) {
        setKycUploading(true);
        let ok = 0, fail = 0;
        for (const item of kycFiles) {
          try {
            const fd = new FormData();
            fd.append('file', item.file);
            await axios.post(`${API}/clients/${data.client_id}/kyc?doc_type=${encodeURIComponent(item.doc_type)}`, fd, {
              withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            ok += 1;
          } catch { fail += 1; }
        }
        setKycUploading(false);
        if (ok > 0) toast.success(`${ok} document${ok === 1 ? '' : 's'} attached`);
        if (fail > 0) toast.warning(`${fail} document${fail === 1 ? '' : 's'} failed to upload — retry from the client's Documents tab`);
      }
      if (onCreated) onCreated({ ...formData, ...data });
      if (addPolicyToggle && data?.client_id && onRequestFollowUpPolicy) {
        onRequestFollowUpPolicy(data.client_id);
      }
      setFormData(emptyForm);
      setAddPolicyToggle(false);
      setKycFiles([]);
      onClose();
    } catch (err) {
      console.error('Create client failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to create client');
    } finally { setSaving(false); }
  };

  const update = (patch) => setFormData((f) => ({ ...f, ...patch }));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) { setFormData(emptyForm); setAddPolicyToggle(false); onClose(); } }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="global-add-client-dialog">
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
          <DialogDescription>Create a client — you can attach a policy right away with the toggle at the bottom.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Upload className="h-4 w-4 text-blue-700" />
            <span>Have many clients? Upload from Excel.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={downloadTemplate} data-testid="client-download-template">
              <Download className="h-3.5 w-3.5 mr-1" /> Download Template
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowBulk(v => !v)} data-testid="client-bulk-toggle">
              {showBulk ? 'Hide bulk upload' : 'Bulk upload'}
            </Button>
          </div>
        </div>

        {showBulk && (
          <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/40 space-y-2" data-testid="client-bulk-panel">
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="client-bulk-file-input" />
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={uploadBulk} disabled={bulkUploading || !bulkFile} className="bg-blue-800 hover:bg-blue-900" data-testid="client-bulk-submit">
                {bulkUploading ? 'Uploading…' : 'Import Clients'}
              </Button>
            </div>
            {bulkResult && (
              <div className="text-xs" data-testid="client-bulk-result">
                <p>
                  <strong className="text-green-700">Inserted:</strong> {bulkResult.inserted}
                  {' · '}<strong className="text-slate-700">Skipped:</strong> {bulkResult.skipped ?? 0}
                  {' · '}<strong className="text-slate-500">Total:</strong> {bulkResult.total_rows ?? 0}
                </p>
                {(bulkResult.errors || []).length > 0 && (
                  <div className="mt-1.5">
                    <p className="font-semibold text-red-700 mb-1">Issues ({bulkResult.errors.length}):</p>
                    <ul className="text-red-700 max-h-56 overflow-y-auto space-y-0.5 border border-red-100 bg-red-50 rounded p-2" data-testid="client-bulk-errors">
                      {bulkResult.errors.map((e, idx) => <li key={`${idx}-${e}`}>• {e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Type</Label>
              <Select value={formData.client_type} onValueChange={(v) => update({ client_type: v })}>
                <SelectTrigger data-testid="global-client-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Corporate">Corporate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => update({ name: e.target.value })} required data-testid="global-client-name-input" />
            </div>
            <div className="space-y-2"><Label>Primary Mobile *</Label>
              <Input inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={formData.primary_mobile} onChange={(e) => update({ primary_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} required data-testid="global-client-mobile-input" />
              <p className="text-[10px] text-slate-500">Exactly 10 digits, starting with 6-9</p>
            </div>
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => update({ email: e.target.value })} data-testid="global-client-email-input" />
            </div>
            <div className="space-y-2"><Label>Date of Birth</Label>
              <Input type="date" value={formData.dob} onChange={(e) => update({ dob: e.target.value })} />
            </div>
            <div className="space-y-2"><Label>Gender</Label>
              <Select value={formData.gender} onValueChange={(v) => update({ gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>PAN</Label>
              <Input value={formData.pan} onChange={(e) => update({ pan: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-2"><Label>Aadhaar</Label>
              <Input value={formData.aadhaar} onChange={(e) => update({ aadhaar: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2"><Label>Address</Label>
              <Input value={formData.address} onChange={(e) => update({ address: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2"><Label>Nominee</Label>
              <Input value={formData.nominee} onChange={(e) => update({ nominee: e.target.value })} />
            </div>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-100 bg-indigo-50/40 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={addPolicyToggle}
              onChange={(e) => setAddPolicyToggle(e.target.checked)}
              className="mt-1 h-4 w-4 accent-blue-800"
              data-testid="global-add-policy-toggle"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Add a policy now?</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                We'll open the Add Policy form pre-filled with this client right after you save.
              </span>
            </span>
          </label>

          {/* Other Financial Holdings — parity with the Clients-menu form. Only "no" triggers cross-sell. */}
          <details className="rounded-lg border border-slate-200 bg-slate-50/60 open:bg-white" data-testid="global-client-holdings-section">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800 select-none">
              Other Financial Holdings <span className="text-xs text-slate-500 font-normal">— quick tags so we don't cross-sell what they already have</span>
            </summary>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                ['mutual_funds', 'Mutual Funds'],
                ['loans', 'Loans'],
                ['fd', 'Fixed Deposit'],
                ['rd', 'Recurring Deposit'],
                ['post_office', 'Post Office Schemes'],
                ['ppf', 'PPF'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-200 bg-white">
                  <span className="text-sm text-slate-800">{label}</span>
                  <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="radiogroup" aria-label={label}>
                    {['yes','no','unknown'].map((v) => {
                      const active = (formData.financial_holdings?.[key] || 'unknown') === v;
                      const cls = active
                        ? (v === 'yes' ? 'bg-emerald-600 text-white' : v === 'no' ? 'bg-orange-600 text-white' : 'bg-slate-500 text-white')
                        : 'text-slate-600 hover:bg-slate-100';
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => update({ financial_holdings: { ...formData.financial_holdings, [key]: v } })}
                          className={`px-2 py-0.5 text-[11px] rounded font-medium capitalize ${cls}`}
                          data-testid={`global-holding-${key}-${v}`}
                        >
                          {v === 'unknown' ? '?' : v}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="px-3 pb-2 text-[11px] text-slate-500">Only <strong>No</strong> triggers a cross-sell suggestion — <strong>?</strong> (unknown) is silent.</p>
          </details>

          <div className="rounded-lg border border-blue-100 bg-blue-50/30 p-3 space-y-2" data-testid="global-client-docs-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">KYC Documents (optional)</p>
                <p className="text-[11px] text-slate-500">Attach PAN, Aadhaar, policy PDFs or other docs. Uploaded after save. PDF/JPG/PNG/WEBP, max 10MB each.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => kycInputRef.current?.click()} data-testid="global-client-kyc-add">
                <Upload className="h-3.5 w-3.5 mr-1" /> Add file
              </Button>
              <input ref={kycInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple className="hidden"
                onChange={(e) => {
                  const arr = Array.from(e.target.files || []).map(f => ({ file: f, doc_type: 'other' }));
                  setKycFiles(prev => [...prev, ...arr]);
                  if (kycInputRef.current) kycInputRef.current.value = '';
                }}
                data-testid="global-client-kyc-file-input" />
            </div>
            {kycFiles.length > 0 && (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded" data-testid="global-client-kyc-list">
                {kycFiles.map((k, i) => (
                  <li key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                    <span className="flex-1 truncate">{k.file.name} · {Math.round(k.file.size / 1024)} KB</span>
                    <select className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white" value={k.doc_type}
                      onChange={(e) => setKycFiles(prev => prev.map((x, j) => j === i ? { ...x, doc_type: e.target.value } : x))}
                      data-testid={`global-client-kyc-type-${i}`}>
                      <option value="pan">PAN</option>
                      <option value="aadhaar">Aadhaar</option>
                      <option value="policy">Policy</option>
                      <option value="other">Other</option>
                    </select>
                    <button type="button" onClick={() => setKycFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-600 hover:text-red-800 text-[11px]" data-testid={`global-client-kyc-remove-${i}`}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={saving || kycUploading} data-testid="global-client-submit-button">
              {saving ? 'Saving…' : (kycUploading ? 'Uploading docs…' : 'Create Client')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientDialog;
